import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #mobileQuery = window.matchMedia('(max-width: 639px)');
  #dragHandle = null;
  #gesture = null;
  #resetTimer = 0;
  #resetTransitionHandler = null;
  #layerContainer = null;
  #activeParent = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMobileSwipe();
    this.#setupMobileLayers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#teardownMobileSwipe();
    this.#teardownMobileLayers();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.#clearSwipeVisuals();
      this.#resetMobileLayers(false);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    if (details === this.refs.details) this.#resetMobileLayers(false);
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  #setupMobileSwipe() {
    const handle = this.querySelector('[data-aoctant-menu-drag-handle]');
    if (!(handle instanceof HTMLElement)) return;

    this.#dragHandle = handle;
    handle.addEventListener('pointerdown', this.#onPointerDown);
    handle.addEventListener('pointermove', this.#onPointerMove);
    handle.addEventListener('pointerup', this.#onPointerUp);
    handle.addEventListener('pointercancel', this.#cancelGesture);
    window.addEventListener('blur', this.#cancelGesture);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    this.#mobileQuery.addEventListener('change', this.#onMobileQueryChange);
  }

  #teardownMobileSwipe() {
    const handle = this.#dragHandle;
    if (handle) {
      handle.removeEventListener('pointerdown', this.#onPointerDown);
      handle.removeEventListener('pointermove', this.#onPointerMove);
      handle.removeEventListener('pointerup', this.#onPointerUp);
      handle.removeEventListener('pointercancel', this.#cancelGesture);
    }
    window.removeEventListener('blur', this.#cancelGesture);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#mobileQuery.removeEventListener('change', this.#onMobileQueryChange);
    this.#finishGesture();
    this.#clearSwipeVisuals();
    this.#dragHandle = null;
  }

  #onPointerDown = (event) => {
    if (!this.#mobileQuery.matches || this.#gesture || !event.isPrimary || !this.isOpen) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const handle = this.#dragHandle;
    if (!handle) return;

    this.#clearSwipeVisuals();
    this.#gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      travel: 0,
      direction: 'pending',
    };

    try {
      handle.setPointerCapture(event.pointerId);
    } catch {}
  };

  #onPointerMove = (event) => {
    const gesture = this.#gesture;
    if (!gesture || event.pointerId !== gesture.pointerId) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const absX = Math.abs(deltaX);

    if (gesture.direction === 'pending') {
      if (Math.max(absX, Math.abs(deltaY)) < 8) return;
      if (deltaY <= 0 || absX > deltaY) {
        this.#cancelGesture();
        return;
      }
      gesture.direction = 'vertical';
      this.refs.menuDrawer.classList.add('aoctant-menu-swipe-active');
    }

    if (deltaY < 0 || absX > deltaY) {
      this.#cancelGesture();
      return;
    }

    event.preventDefault();
    gesture.travel = deltaY;
    this.refs.menuDrawer.style.setProperty('--aoctant-menu-swipe-offset', `${deltaY}px`);
  };

  #onPointerUp = (event) => {
    if (!this.#gesture || event.pointerId !== this.#gesture.pointerId) return;

    const gesture = this.#finishGesture();
    if (!gesture) return;

    const sheetHeight = this.refs.menuDrawer.getBoundingClientRect().height;
    const shouldClose =
      gesture.direction === 'vertical' &&
      (gesture.travel >= 80 || gesture.travel >= sheetHeight * 0.18);

    if (!shouldClose) {
      this.#resetPanel(gesture.travel);
      return;
    }

    this.#clearResetTimer();
    this.refs.menuDrawer.classList.remove('aoctant-menu-swipe-active', 'aoctant-menu-swipe-resetting');
    this.close();
    window.setTimeout(() => this.#clearSwipeVisuals(), 650);
  };

  #resetPanel(travel) {
    if (travel <= 0) {
      this.#clearSwipeVisuals();
      return;
    }

    const panel = this.refs.menuDrawer;
    this.#clearResetTimer();
    panel.classList.remove('aoctant-menu-swipe-active');
    panel.classList.add('aoctant-menu-swipe-resetting');
    panel.style.setProperty('--aoctant-menu-swipe-offset', `${travel}px`);

    requestAnimationFrame(() => {
      panel.style.setProperty('--aoctant-menu-swipe-offset', '0px');
    });

    const finishReset = () => this.#clearSwipeVisuals();
    this.#resetTransitionHandler = (event) => {
      if (event.target === panel && event.propertyName === 'transform') finishReset();
    };
    panel.addEventListener('transitionend', this.#resetTransitionHandler);
    this.#resetTimer = window.setTimeout(finishReset, 360);
  }

  #cancelGesture = () => {
    const gesture = this.#finishGesture();
    if (gesture) this.#resetPanel(gesture.travel);
  };

  #finishGesture() {
    const gesture = this.#gesture;
    this.#gesture = null;
    if (!gesture) return null;

    const handle = this.#dragHandle;
    try {
      if (handle?.hasPointerCapture(gesture.pointerId)) handle.releasePointerCapture(gesture.pointerId);
    } catch {}
    return gesture;
  }

  #clearResetTimer() {
    if (!this.#resetTimer) return;
    window.clearTimeout(this.#resetTimer);
    this.#resetTimer = 0;
  }

  #clearSwipeVisuals() {
    const panel = this.refs.menuDrawer;
    this.#clearResetTimer();
    if (this.#resetTransitionHandler) {
      panel.removeEventListener('transitionend', this.#resetTransitionHandler);
      this.#resetTransitionHandler = null;
    }
    panel.classList.remove('aoctant-menu-swipe-active', 'aoctant-menu-swipe-resetting');
    panel.style.removeProperty('--aoctant-menu-swipe-offset');
  }

  #onVisibilityChange = () => {
    if (document.hidden) this.#cancelGesture();
  };

  #onMobileQueryChange = (event) => {
    if (!event.matches) {
      this.#cancelGesture();
      this.#resetMobileLayers(false);
    }
  };

  #setupMobileLayers() {
    const container = this.querySelector('[data-aoctant-mobile-menu-layers]');
    if (!(container instanceof HTMLElement)) return;

    this.#layerContainer = container;
    container.addEventListener('click', this.#onLayerClick);
    this.#resetMobileLayers(false);
  }

  #teardownMobileLayers() {
    this.#layerContainer?.removeEventListener('click', this.#onLayerClick);
    this.#resetMobileLayers(false);
    this.#layerContainer = null;
  }

  #onLayerClick = (event) => {
    if (!this.#mobileQuery.matches || !(event.target instanceof Element)) return;

    const parent = event.target.closest('[data-aoctant-mobile-menu-parent]');
    if (parent instanceof HTMLButtonElement) {
      const targetId = parent.getAttribute('aria-controls');
      const child = targetId ? document.getElementById(targetId) : null;
      if (child instanceof HTMLElement && this.contains(child)) this.#openMobileChild(parent, child);
      return;
    }

    if (event.target.closest('[data-aoctant-mobile-menu-back]')) this.#resetMobileLayers(true);
  };

  #openMobileChild(parent, child) {
    const container = this.#layerContainer;
    const root = container?.querySelector('[data-aoctant-mobile-menu-root]');
    if (!container || !(root instanceof HTMLElement)) return;

    this.#resetMobileLayers(false);
    this.#activeParent = parent;
    parent.setAttribute('aria-expanded', 'true');
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('inert', '');
    child.setAttribute('aria-hidden', 'false');
    child.removeAttribute('inert');
    child.classList.add('aoctant-mobile-menu__layer--active');
    container.classList.add('aoctant-mobile-menu--child-open');
    child.querySelector('[data-aoctant-mobile-menu-back]')?.focus();
  }

  #resetMobileLayers(restoreFocus) {
    const container = this.#layerContainer;
    if (!container) return;

    const previousParent = this.#activeParent;
    const root = container.querySelector('[data-aoctant-mobile-menu-root]');
    container.classList.remove('aoctant-mobile-menu--child-open');
    if (root instanceof HTMLElement) {
      root.setAttribute('aria-hidden', 'false');
      root.removeAttribute('inert');
    }

    container.querySelectorAll('[data-aoctant-mobile-menu-parent]').forEach((parent) => {
      parent.setAttribute('aria-expanded', 'false');
    });
    container.querySelectorAll('[data-aoctant-mobile-menu-child]').forEach((child) => {
      child.classList.remove('aoctant-mobile-menu__layer--active');
      child.setAttribute('aria-hidden', 'true');
      child.setAttribute('inert', '');
    });

    this.#activeParent = null;
    if (restoreFocus && previousParent instanceof HTMLElement) previousParent.focus();
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
